export const DEFAULT_FONT_COLOR = '#000000';
export const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';

export const DESCRIPTION_MAX = 500;
export const NAME_MAX = 30;

// the starting tier counts towards the cap
export const MAX_TIERS = 6;

// GET /tier/list status filter. Omitting it returns drafts and published together.
export const TIER_STATUS = {
  DRAFT: 0,
  PUBLISHED: 100,
};

// Rank 1 is the highest tier and equals the submission order: the first row sent is rank 1.
// The last row is the default tier every creator lands on, and it carries a fixed condition.
// The API derives rank from that order and never receives a rank field.

// badge heights the list and previews render, with the font and icon scaled to each
export const PREVIEW_SIZES = [
  { size: 40, font: 16, icon: 20, padding: 16, gap: 8 },
  { size: 32, font: 14, icon: 16, padding: 12, gap: 6 },
  { size: 24, font: 12, icon: 12, padding: 8, gap: 4 },
];

export const UNIT = {
  BOOLEAN: 'boolean',
  COUNT: 'count',
  CURRENCY: 'currency',
  ENUM: 'enum',
};

// operators each unit allows, per the metrics table
const OPERATORS_BY_UNIT = {
  [UNIT.BOOLEAN]: ['='],
  [UNIT.COUNT]: ['>=', '>'],
  [UNIT.CURRENCY]: ['>=', '>'],
  [UNIT.ENUM]: ['=', '/='],
};

// the account types used elsewhere in the app, see FilterDropdown.vue
const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Storefront', value: 1 },
  { label: 'Links', value: 2 },
];

// the 14 metrics, in the order given by the table.
// `noun` and `affirm` are only used to phrase a saved condition on the tier list.
export const CONDITION_FIELDS = [
  {
    value: 'account_creation',
    label: 'Account Creation (Default Tier)',
    unit: UNIT.BOOLEAN,
    affirm: 'Create account',
  },
  {
    value: 'tiktok_account_connected',
    label: 'TikTok Account Connected',
    unit: UNIT.BOOLEAN,
    affirm: 'Connect TikTok account',
  },
  {
    value: 'instagram_account_connected',
    label: 'Instagram Account Connected',
    unit: UNIT.BOOLEAN,
    affirm: 'Connect Instagram account',
  },
  { value: 'branded_content', label: 'Branded Content', unit: UNIT.COUNT, noun: 'branded content' },
  { value: 'collections_created', label: 'Collections Created', unit: UNIT.COUNT, noun: 'collections' },
  { value: 'posts_created', label: 'Posts Created', unit: UNIT.COUNT, noun: 'posts' },
  { value: 'products_added', label: 'Products Added', unit: UNIT.COUNT, noun: 'products' },
  { value: 'featured_items', label: 'Featured Items', unit: UNIT.COUNT, noun: 'featured items' },
  { value: 'copied_shortlinks', label: 'Copied Shortlinks', unit: UNIT.COUNT, noun: 'copied shortlinks' },
  { value: 'account_type', label: 'Account Type', unit: UNIT.ENUM, options: ACCOUNT_TYPE_OPTIONS },
  { value: 'storefront_views', label: 'Storefront Views', unit: UNIT.COUNT, noun: 'views' },
  { value: 'total_clicks', label: 'Total Clicks', unit: UNIT.COUNT, noun: 'clicks' },
  { value: 'total_sales_value', label: 'Total Sales Value', unit: UNIT.CURRENCY, noun: 'Sales value' },
  { value: 'total_sales', label: 'Total Sales', unit: UNIT.COUNT, noun: 'sales' },
];

let uid = 0;

function nextKey(prefix) {
  uid += 1;
  return `${prefix}-${uid}`;
}

export function createCondition() {
  return {
    key: nextKey('condition'),
    field: '',
    operator: '',
    value: '',
  };
}

// the one condition the starting tier always carries: signing up
export function startingCondition() {
  return { ...createCondition(), field: 'account_creation', operator: '=', value: true };
}

export function createTier(is_starting = false) {
  return {
    key: nextKey('tier'),
    id: null,
    // a placeholder, syncRanks derives the real value from the position
    rank: 1,
    is_starting,
    status: TIER_STATUS.DRAFT,
    name: '',
    description: '',
    icon: '',
    icon_file: null,
    icon_removed: false,
    font_color: DEFAULT_FONT_COLOR,
    background_color: DEFAULT_BACKGROUND_COLOR,
    conditions: is_starting ? [startingCondition()] : [createCondition()],
    creators_count: null,
    last_edited: null,
  };
}

// the default tier: fixed condition, always the bottom row. it can still be deleted, in
// which case whatever ends up last takes over the role.
export function isStartingTier(tier) {
  return !!tier.is_starting;
}

/**
 * The array position is the ranking: index 0 is rank 1 and the highest tier, the last row
 * is the default tier. Rewritten after every add, delete or reorder so both stay in step
 * with what the batch endpoints derive from the submission order.
 */
export function syncRanks(tiers) {
  tiers.forEach((tier, index) => {
    tier.rank = index + 1;
    tier.is_starting = index === tiers.length - 1;
  });
  return tiers;
}

// the metric and operator carry over to the lower tiers, the threshold never does
export function copyConditionShape(condition) {
  return {
    ...createCondition(),
    field: condition.field,
    operator: condition.operator,
    value: defaultValueFor(condition.field),
  };
}

function mirroredRows(tier) {
  return JSON.stringify(tier.conditions.map((c) => [c.field, c.operator, defaultValueFor(c.field)]));
}

function actualRows(tier) {
  return JSON.stringify(tier.conditions.map((c) => [c.field, c.operator, c.value]));
}

/**
 * True while every lower tier is still an untouched copy of the top tier, which also covers
 * the case where nothing has been entered anywhere yet. Re-checked on entering the conditions
 * step, so returning through Back stops the copying as soon as a lower tier differs.
 */
export function isMirrorIntact(tiers) {
  const others = tiers.filter((tier) => !isStartingTier(tier));
  if (others.length < 2) return true;

  const expected = mirroredRows(others[0]);
  return others.slice(1).every((tier) => actualRows(tier) === expected);
}

/**
 * Only the editable content, so a preview toggle or a rank rewrite is not an edit.
 * Used to tell whether the batch editor has unsaved work.
 */
function conditionsSignature(tier) {
  return JSON.stringify(tier.conditions.map((condition) => [condition.field, condition.operator, condition.value]));
}

function appearanceSignature(tier) {
  return JSON.stringify([tier.description, tier.icon, tier.icon_removed, tier.font_color, tier.background_color]);
}

export function tierSignature(tier) {
  return JSON.stringify([tier.name.trim(), appearanceSignature(tier), conditionsSignature(tier)]);
}

function displayName(tier) {
  return tier.name.trim() || `Rank ${tier.rank}`;
}

/**
 * Plain english list of what a publish would change, for the confirmation dialog.
 * Rows are matched by id, so an unsaved tier always reads as an addition.
 */
export function publishSummary(before, after) {
  const lines = [];
  const previous = new Map(before.filter((tier) => tier.id !== null).map((tier) => [tier.id, tier]));

  after.forEach((tier) => {
    const old = tier.id === null ? null : previous.get(tier.id);

    if (!old) {
      lines.push(`${displayName(tier)} Tier added at Rank ${tier.rank}`);
      return;
    }
    if (old.name.trim() !== tier.name.trim()) {
      lines.push(`${displayName(old)} Tier renamed to ${displayName(tier)}`);
    }
    if (old.rank !== tier.rank) lines.push(`${displayName(tier)} Tier changed to Rank ${tier.rank}`);
    if (conditionsSignature(old) !== conditionsSignature(tier)) {
      lines.push(`${displayName(tier)} Tier conditions changed`);
    }
    if (appearanceSignature(old) !== appearanceSignature(tier)) {
      lines.push(`${displayName(tier)} Tier appearance changed`);
    }
  });

  before.forEach((tier) => {
    if (tier.id !== null && !after.some((item) => item.id === tier.id)) {
      lines.push(`${displayName(tier)} Tier removed`);
    }
  });

  return lines;
}

/**
 * Threshold check addressed by array position, for the batch editor.
 * `syncRanks` keeps rank in step with the positions, so the comparison stays rank based.
 */
export function conditionIssue(tiers, tier_index, condition_index) {
  const tier = tiers[tier_index];
  if (!tier) return '';
  return thresholdIssue(
    tier,
    tiers.filter((_, index) => index !== tier_index),
    condition_index
  );
}

// deep enough to keep form edits off the loaded row, shallow enough to keep the picked File
export function cloneTier(tier) {
  return {
    ...tier,
    conditions: tier.conditions.map((condition) => ({ ...condition })),
  };
}

/**
 * Whether tiers should be visible at all: the merchant has the feature (is_enable_tier on /me)
 * and this user is allowed to see it (settingCustomization.creatorTier).
 *
 * Every entry point routes its visibility through here, so the whole feature switches off from
 * one place and a field path only has to be corrected once if it ever moves. Takes the store
 * rather than importing it, to keep this module free of app wiring.
 *
 * The permission is compared against 0 rather than 100, matching checkPermission in the router:
 * a payload that predates the permission falls back to is_enable_tier alone instead of hiding
 * the feature from everyone.
 */
export function isTierEnabled(store) {
  const user_data = store?.state?.user_data;
  if (!user_data?.merchant_account?.is_enable_tier) return false;
  return user_data?.permission_list?.settingCustomization?.creatorTier?.permission !== 0;
}

/**
 * Whether this user may create, edit, delete or publish tiers. Viewing is isTierEnabled;
 * this is the narrower right behind the Create / Edit and add tier / Publish controls.
 */
export function canEditTiers(store) {
  if (!isTierEnabled(store)) return false;
  const permissions = store?.state?.user_data?.permission_list;
  return permissions?.settingCustomization?.create0rEditcreatorTier?.permission !== 0;
}

/**
 * Query for GET /tier/list. Only the documented params are sent, and an empty
 * status is left out so the endpoint returns both drafts and published rows.
 */
export function tierListQuery(options = {}) {
  const query = {
    page: Math.max(1, options.page || 1),
    per_page: Math.min(100, Math.max(1, options.per_page || 20)),
    sort_key: options.sort_key || 'rank',
    sort_val: options.sort_val || 'asc',
    keyword: options.keyword || '',
  };

  if (options.status === TIER_STATUS.DRAFT || options.status === TIER_STATUS.PUBLISHED) {
    query.status = options.status;
  }

  return query;
}

// count and currency values bind to a text input, booleans and enums stay native
function conditionValueFromApi(item) {
  const unit = fieldUnit(item.field);
  if (unit === UNIT.BOOLEAN || unit === UNIT.ENUM) return item.value;
  return item.value === null || item.value === undefined ? '' : String(item.value);
}

/**
 * Maps one row of GET /tier/list onto the shape the components work with.
 * The only place in the feature that reads the API payload.
 */
export function tierFromApi(raw, index = 0) {
  // note `condition` is singular on the wire, matching the create/update body
  const conditions = (raw.condition || []).map((item) => ({
    ...createCondition(),
    field: item.field ?? '',
    operator: item.operator ?? '',
    value: conditionValueFromApi(item),
  }));

  const rank = raw.rank ?? index + 1;
  // the API is the authority on which row is the default one, there is no way to infer it
  // from a single row: sortTiersByRank puts it last and syncRanks confirms it on edit
  const is_starting = !!raw.is_starting_tier;

  return {
    key: `tier-api-${raw.id ?? index}`,
    id: raw.id ?? null,
    rank,
    is_starting,
    status: raw.status ?? TIER_STATUS.PUBLISHED,
    name: raw.name ?? '',
    description: raw.description ?? '',
    icon: raw.icon_url ?? '',
    icon_file: null,
    icon_removed: false,
    font_color: raw.font_color_hex || DEFAULT_FONT_COLOR,
    background_color: raw.background_color_hex || DEFAULT_BACKGROUND_COLOR,
    conditions: conditions.length ? conditions : [is_starting ? startingCondition() : createCondition()],
    creators_count: raw.creator_count,
    last_edited: raw.updated_at ?? null,
    published_at: raw.published_at ?? null,
  };
}

/**
 * A published tier that was edited after going live still has changes creators cannot see.
 * The status column stays Active in that case, so the list flags it separately.
 */
export function hasUnpublishedChanges(tier) {
  if (tier.status !== TIER_STATUS.PUBLISHED || !tier.published_at || !tier.last_edited) return false;
  return tier.last_edited > tier.published_at;
}

export const SAVE_DRAFT_URL = '/tier/save-tiers-to-draft';
export const SAVE_PUBLISH_URL = '/tier/save-tiers-and-publish';

/**
 * Body for POST /tier/save-tiers-to-draft and POST /tier/save-tiers-and-publish.
 *
 * Parallel form-data arrays, one entry per tier aligned by index. Every array gets an entry
 * for every row, including the empty ones, or the columns would slide out of alignment.
 *
 * `rank` is deliberately not sent: the endpoints read it from the submission order, so the
 * array must be handed over already sorted with rank 1 first and the default tier last.
 *
 * Icons follow two channels: `icon_file[]` carries a newly picked file, `exist_icon_url[]`
 * keeps an icon that is already uploaded. Leaving both empty on a row clears its icon.
 */
export function tiersToFormData(tiers) {
  const form = new FormData();

  tiers.forEach((tier) => {
    // an existing row is updated, an empty id means create
    form.append('tier_id[]', tier.id === null || tier.id === undefined ? '' : String(tier.id));
    form.append('name[]', tier.name.trim());
    form.append('condition[]', JSON.stringify(conditionsToApi(tier)));
    form.append('font_color_hex[]', tier.font_color);
    form.append('background_color_hex[]', tier.background_color);
    form.append('description[]', tier.description || '');

    const keeps_icon = !tier.icon_file && !tier.icon_removed && isRemoteIcon(tier.icon);
    form.append('exist_icon_url[]', keeps_icon ? tier.icon : '');
    form.append('icon_file[]', tier.icon_file || new Blob([]), tier.icon_file?.name ?? '');
  });

  return form;
}

// a local preview is a blob: URL, which means the file itself travels in icon_file
function isRemoteIcon(icon) {
  return !!icon && !icon.startsWith('blob:');
}

// the payload wants real numbers and booleans, while the form holds input strings
function conditionValueToApi(condition) {
  const unit = fieldUnit(condition.field);
  if (unit === UNIT.BOOLEAN) return condition.value === true;
  if (unit === UNIT.ENUM) return Number(condition.value);
  return Number(condition.value);
}

// the starting tier sends its one fixed condition, everyone else sends what was filled in
export function conditionsToApi(tier) {
  return conditionsOf(tier)
    .filter(isConditionComplete)
    .map((condition) => ({
      field: condition.field,
      operator: condition.operator,
      value: conditionValueToApi(condition),
    }));
}

// rank 1 first, since rank 1 is the highest tier. the default tier is forced to the bottom
// whatever rank it reports, because that position is what the endpoints read it from.
export function sortTiersByRank(tiers) {
  return [...tiers].sort((a, b) => {
    if (isStartingTier(a) !== isStartingTier(b)) return isStartingTier(a) ? 1 : -1;
    return (a.rank ?? 0) - (b.rank ?? 0);
  });
}

export function fieldOf(field) {
  return CONDITION_FIELDS.find((item) => item.value === field);
}

export function fieldUnit(field) {
  return fieldOf(field)?.unit || '';
}

export function fieldOptions(field) {
  return fieldOf(field)?.options || [];
}

export function operatorsFor(field) {
  return OPERATORS_BY_UNIT[fieldUnit(field)] || [];
}

// what a freshly picked metric starts on, booleans have no meaningful empty state
export function defaultValueFor(field) {
  return fieldUnit(field) === UNIT.BOOLEAN ? true : '';
}

// only count and currency metrics carry a threshold that can be compared across tiers
export function isThresholdField(field) {
  const unit = fieldUnit(field);
  return unit === UNIT.COUNT || unit === UNIT.CURRENCY;
}

function isEmptyValue(value) {
  return value === '' || value === null || value === undefined;
}

export function isConditionComplete(condition) {
  return !!condition.field && !!condition.operator && !isEmptyValue(condition.value);
}

// an untouched row, safe to ignore rather than flag
export function isConditionBlank(condition) {
  return !condition.field && !condition.operator && isEmptyValue(condition.value);
}

// Conditions is required: a tier needs one usable rule and no half filled rows
export function areConditionsValid(tier) {
  if (isStartingTier(tier)) return true;
  return (
    tier.conditions.some(isConditionComplete) &&
    !tier.conditions.some((condition) => !isConditionComplete(condition) && !isConditionBlank(condition))
  );
}

/**
 * The rows a tier really has. The starting tier's condition is fixed and its editor never
 * shows rows, so a payload that omits it falls back to the sign up condition rather than
 * leaving the tier looking condition-less.
 */
function conditionsOf(tier) {
  if (!isStartingTier(tier)) return tier.conditions;
  return tier.conditions.some(isConditionComplete) ? tier.conditions : [startingCondition()];
}

// a metric may only be used once per tier, so the picker disables the ones already taken
export function isFieldTaken(tier, condition, field) {
  return tier.conditions.some((other) => other !== condition && other.field === field);
}

function threshold(condition) {
  if (isEmptyValue(condition.value)) return null;
  const number = Number(condition.value);
  return Number.isNaN(number) ? null : number;
}

/**
 * Cross tier threshold check for one row of the tier being edited.
 *
 * A lower ranked tier must never ask for more than a higher ranked one. `others` is every
 * other tier, each carrying its own rank, so a single tier can be validated on its own.
 * Returns the message to show under the row, or '' when the row is fine.
 */
export function thresholdIssue(tier, others, condition_index) {
  const condition = tier.conditions[condition_index];
  if (!condition || !condition.field || !isThresholdField(condition.field)) return '';

  const value = threshold(condition);
  if (value === null) return '';

  for (const other of others) {
    if (isStartingTier(other)) continue;

    const match = other.conditions.find((item) => item.field === condition.field && threshold(item) !== null);
    if (!match) continue;

    const other_value = threshold(match);
    const other_name = other.name.trim() || `Rank ${other.rank}`;

    // rank 1 is the highest tier, so a smaller rank must never ask for less
    if (other.rank > tier.rank && value <= other_value) {
      return `Must be higher than ${other_name} (${match.value}).`;
    }
    if (other.rank < tier.rank && value >= other_value) {
      return `Must be lower than ${other_name} (${match.value}).`;
    }
  }

  return '';
}

export function hasThresholdIssues(tier, others) {
  return tier.conditions.some((condition, index) => thresholdIssue(tier, others, index));
}

function withCommas(value) {
  const number = Number(value);
  return Number.isNaN(number) ? String(value) : number.toLocaleString('en-US');
}

// turns one saved condition into the sentence shown on the tier list
export function conditionText(condition) {
  const field = fieldOf(condition.field);
  if (!field) return '';

  if (field.unit === UNIT.BOOLEAN) {
    return condition.value === false ? `Do not ${field.affirm.toLowerCase()}` : field.affirm;
  }

  if (field.unit === UNIT.ENUM) {
    const option = (field.options || []).find((item) => item.value === condition.value);
    if (!option) return '';
    return `${field.label} is${condition.operator === '/=' ? ' not' : ''} ${option.label}`;
  }

  if (isEmptyValue(condition.value)) return '';
  return `${condition.operator === '>=' ? 'Earn at least' : 'Earn over'} ${withCommas(condition.value)} ${field.noun}`;
}

export function tierConditionTexts(tier) {
  return conditionsOf(tier).filter(isConditionComplete).map(conditionText).filter(Boolean);
}
